import React from "react";
import { Box, Modal, Backdrop, IconButton, Fade } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { styled } from "@mui/material/styles";
import { attachTableCellTooltip } from "@/utils/tsvTable";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const SectionBox = styled(Box, {
  shouldForwardProp: prop => prop !== "accentColor"
})(({
  theme,
  accentColor
}) => ({
  marginBottom: theme.spacing(4),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  borderLeft: `4px solid ${accentColor || theme.palette.primary.main}`,
  boxShadow: theme.shadows[1],
  overflowWrap: "break-word",
  wordBreak: "break-word",
  hyphens: "auto"
}));
export default function RegulationMainContent({
  html,
  accentColor
}) {
  const containerRef = React.useRef(null);
  const [images, setImages] = React.useState([]);
  const [activeIdx, setActiveIdx] = React.useState(null);

  // Normalize the HTML so the published view matches the editor's
  // figure-based layout even for content saved before the figure wrapper
  // existed. We move any `.editor-img-text` that lives inside an
  // `.editor-img-row[data-image-text='true']` (legacy) — or that sits as a
  // sibling of such a row without a figure parent — into a fresh
  // `.editor-img-figure` wrapper so the existing CSS rules apply.
  const normalizedHTML = React.useMemo(() => {
    if (!html) return html;
    if (typeof window === "undefined" || typeof document === "undefined") {
      return html;
    }
    const tmp = document.createElement("div");
    tmp.innerHTML = html;

    // Heal briefs saved before the sanitizer stripped source-document
    // geometry: bare (externally pasted) tables still carry inline
    // widths / pt font sizes from Word or Docs, rendering at ~55% of
    // the body with shrunken text. In-app .pasted-table-wrap tables
    // keep their inline col widths untouched.
    tmp.querySelectorAll("table").forEach(tbl => {
      if (tbl.closest(".pasted-table-wrap")) return;
      const els = [tbl, ...Array.from(tbl.querySelectorAll("td, th, tr, col, colgroup"))];
      for (const el of els) {
        if (el.style) {
          el.style.width = "";
          el.style.minWidth = "";
          el.style.maxWidth = "";
          el.style.height = "";
          el.style.fontSize = "";
          el.style.fontFamily = "";
        }
        el.removeAttribute("width");
        el.removeAttribute("height");
        if (el.getAttribute("style")?.trim() === "") {
          el.removeAttribute("style");
        }
      }
      tbl.querySelectorAll("span[style]").forEach(sp => {
        sp.style.fontSize = "";
        sp.style.fontFamily = "";
        if (sp.getAttribute("style")?.trim() === "") {
          sp.removeAttribute("style");
        }
      });
    });
    const rows = Array.from(tmp.querySelectorAll(".editor-img-row[data-image-text='true']"));
    rows.forEach(row => {
      const parent = row.parentElement;
      if (!parent) return;
      if (parent.classList.contains("editor-img-figure")) return;
      let txt = row.querySelector(":scope > .editor-img-text") || null;
      if (!txt) {
        const next = row.nextElementSibling;
        if (next && next.classList.contains("editor-img-text")) {
          txt = next;
        }
      }
      const figure = document.createElement("div");
      figure.className = "editor-img-figure";
      figure.setAttribute("contenteditable", "false");
      const side = row.getAttribute("data-text-side") || txt && txt.getAttribute("data-text-side") || "right";
      figure.setAttribute("data-text-side", side);
      row.removeAttribute("data-text-side");
      parent.insertBefore(figure, row);
      figure.appendChild(row);
      if (txt) {
        figure.appendChild(txt);
      }
    });
    // Make sure every anchor exposes its href via the native `title`
    // attribute so the browser shows the URL on hover.
    Array.from(tmp.querySelectorAll("a[href]")).forEach(a => {
      if (!a.getAttribute("title")) {
        a.setAttribute("title", a.getAttribute("href") || "");
      }
    });
    // Strip `contenteditable` from persisted table markup so the
    // published view (a) doesn't appear editable and (b) supports
    // native cross-cell text selection. Cells are stored with
    // `contenteditable="true"` for the in-editor experience, which
    // breaks drag-selection on a read-only page because each cell
    // is its own editable boundary.
    Array.from(tmp.querySelectorAll(".pasted-table-wrap, .pasted-table-wrap [contenteditable]")).forEach(el => {
      el.removeAttribute("contenteditable");
    });
    // Performance hints for images: lazy-load offscreen images and
    // let the browser decode asynchronously so the main thread stays
    // free during scroll. The browser caches decoded bitmaps once
    // they've been shown, so subsequent paints are cheap.
    Array.from(tmp.querySelectorAll("img")).forEach(img => {
      if (!img.hasAttribute("loading")) img.setAttribute("loading", "lazy");
      if (!img.hasAttribute("decoding")) img.setAttribute("decoding", "async");
    });
    return tmp.innerHTML;
  }, [html]);

  // Collect images and wire up click-to-open behavior whenever html changes
  React.useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const refreshImages = () => {
      const liveImgs = Array.from(root.querySelectorAll(".editor-img-row img, img.editor-img"));
      setImages(liveImgs.map(img => ({
        src: img.currentSrc || img.src,
        alt: img.alt || ""
      })));
      liveImgs.forEach(img => {
        img.style.cursor = "zoom-in";
      });
      return liveImgs;
    };
    refreshImages();
    // Use event delegation on the container. Re-query the live image list
    // at click time so we never rely on stale node references that may
    // have been replaced by a re-render of dangerouslySetInnerHTML.
    const onClick = e => {
      const t = e.target;
      if (!t) return;
      // Ignore clicks on the editable side-text area so users can
      // interact with the caption text normally.
      if (t.closest(".editor-img-text")) return;
      // Match the image directly, or the surrounding wrap (so clicks
      // on padding around an `object-fit: contain` image still open
      // the lightbox).
      let img = t.closest(".editor-img-row img, img.editor-img");
      if (!img) {
        const wrap = t.closest(".editor-img-wrap, .editor-img-row, .editor-img-figure");
        if (wrap) {
          img = wrap.querySelector("img");
        }
      }
      if (!img || !root.contains(img)) return;
      const liveImgs = refreshImages();
      const idx = liveImgs.indexOf(img);
      if (idx < 0) return;
      e.preventDefault();
      e.stopPropagation();
      setActiveIdx(idx);
    };
    root.addEventListener("click", onClick, true);
    return () => {
      root.removeEventListener("click", onClick, true);
    };
  }, [html, normalizedHTML]);

  // Keyboard navigation in the lightbox
  React.useEffect(() => {
    if (activeIdx === null) return;
    const onKey = e => {
      if (e.key === "Escape") {
        setActiveIdx(null);
      } else if (e.key === "ArrowRight") {
        setActiveIdx(i => i === null ? null : (i + 1) % Math.max(images.length, 1));
      } else if (e.key === "ArrowLeft") {
        setActiveIdx(i => i === null ? null : (i - 1 + images.length) % Math.max(images.length, 1));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activeIdx, images.length]);

  // Custom 500ms tooltip for truncated table cells (matches the
  // editor's behavior). Reads `data-full-text` written by `recalcCol`
  // when a column was wrap-disabled at edit time.
  React.useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    // When the published HTML was regenerated from the structured
    // table data (`tableDataToHtml`), the per-cell `data-full-text`
    // attribute that the editor writes during truncation is missing.
    // Detect actually-overflowing cells via DOM measurement and
    // backfill the attribute so the tooltip can fire.
    const annotateOverflowingCells = () => {
      root.querySelectorAll(".pasted-table-wrap[data-wrap='off'] table.pasted-table th, " + ".pasted-table-wrap[data-wrap='off'] table.pasted-table td").forEach(cell => {
        if (cell.hasAttribute("data-full-text")) return;
        // scrollWidth > clientWidth ⇒ ellipsis is currently active.
        if (cell.scrollWidth > cell.clientWidth + 1) {
          const text = (cell.textContent || "").trim();
          if (text) cell.setAttribute("data-full-text", text);
        }
      });
    };
    annotateOverflowingCells();
    // Re-run on resize because column widths can shift with viewport.
    const onResize = () => annotateOverflowingCells();
    window.addEventListener("resize", onResize);
    const detach = attachTableCellTooltip(root);
    return () => {
      window.removeEventListener("resize", onResize);
      detach();
    };
  }, [normalizedHTML]);
  return /*#__PURE__*/_jsxs(SectionBox, {
    accentColor: accentColor,
    children: [/*#__PURE__*/_jsx(Box, {
      sx: {
        fontSize: "1rem",
        lineHeight: 1.6,
        color: t => t.palette.text.primary,
        "& a, & .editor-link": {
          color: accentColor ? accentColor : t => t.palette.primary.main,
          textDecoration: "underline"
        },
        "& .reg-header": {
          display: "block",
          fontWeight: 700,
          fontSize: "2rem",
          backgroundColor: "transparent",
          margin: t => `${t.spacing(2)} 0 ${t.spacing(1)} 0`,
          color: t => t.palette.text.primary,
          "&::before": {
            content: 'attr(data-header) ". "',
            color: accentColor ? accentColor : t => t.palette.primary.main,
            fontWeight: 700,
            fontSize: "2rem"
          }
        },
        '& p[data-body="true"]': {
          paddingLeft: t => t.spacing(2),
          textIndent: t => t.spacing(1)
        },
        '& ul[data-gen] > li[data-body="true"]': {
          paddingLeft: t => t.spacing(3)
        },
        "& ul[data-gen], & ol[data-gen]": {
          paddingLeft: t => t.spacing(3),
          margin: t => `${t.spacing(1)} 0`
        },
        "& ul[data-gen] > li::marker, & ol[data-gen] > li::marker": {
          color: accentColor ? accentColor : t => t.palette.primary.main
        },
        "& .editor-img-wrap": {
          display: "inline-block",
          verticalAlign: "middle",
          margin: t => t.spacing(0.5, 1),
          textAlign: "center"
        },
        "& .img-remove-btn": {
          display: "none !important"
        },
        // ── Pasted table styling ──
        "& .pt-toolbar": {
          display: "none !important"
        },
        "& .pasted-table-wrap": {
          maxWidth: "100%",
          margin: t => `${t.spacing(2)} 0`,
          // Re-enable native text selection on a read-only
          // page (the editor variant disables this so cell
          // selection chrome can drive copy/paste). Without
          // this, dragging across cells gets clipped at the
          // first cell's editable boundary.
          userSelect: "text",
          WebkitUserSelect: "text"
        },
        "& .pasted-table-scroll": {
          overflowX: "auto",
          maxWidth: "100%"
        },
        "& table.pasted-table, & table": {
          borderCollapse: "separate",
          borderSpacing: 0,
          borderRadius: "8px",
          overflow: "hidden",
          lineHeight: 1.4,
          border: "2px solid #555"
        },
        // The app's own TSV tables are dense and column-sized:
        // fixed layout + compact type.
        "& table.pasted-table": {
          tableLayout: "fixed",
          fontSize: "0.875rem"
        },
        // Externally pasted tables (bare <table>, source widths
        // stripped by the sanitizer): span the body at body
        // size and let content size the columns.
        "& table:not(.pasted-table)": {
          width: "100%",
          tableLayout: "auto",
          fontSize: "1rem"
        },
        "& table.pasted-table th, & table.pasted-table td, & table th, & table td": {
          borderRight: "1px solid #e0e0e0",
          borderBottom: "1px solid #e0e0e0",
          padding: "6px 10px",
          textAlign: "left",
          overflowWrap: "anywhere",
          wordBreak: "break-word",
          whiteSpace: "normal",
          // Explicit selection enablement so dragging
          // across cells builds a single multi-cell
          // selection (overrides any inherited
          // user-select:none from app-level resets).
          userSelect: "text",
          WebkitUserSelect: "text",
          cursor: "text"
        },
        "& .pasted-table-wrap[data-wrap='off'] table.pasted-table td, & .pasted-table-wrap[data-wrap='off'] table.pasted-table th": {
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap"
        },
        "& table th:last-child, & table td:last-child": {
          borderRight: "none"
        },
        "& table tr:last-child td, & table tr:last-child th": {
          borderBottom: "none"
        },
        "& table.pasted-table tr.default-header td": {
          fontWeight: 700,
          fontSize: "1.05em",
          borderBottom: "3px solid #333",
          backgroundColor: t => t.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"
        },
        "& table.pasted-table tbody tr:nth-of-type(odd)": {
          backgroundColor: "#ffffff"
        },
        "& table.pasted-table tbody tr:nth-of-type(even)": {
          backgroundColor: "rgba(0,0,0,0.075)"
        },
        "& table.pasted-table thead th, & table thead th": {
          fontWeight: 700,
          fontSize: "1.05em",
          borderBottom: "3px solid #333",
          backgroundColor: t => t.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"
        },
        "& table thead th.label-cell:empty::before": {
          content: "attr(data-placeholder)",
          opacity: 0.4,
          fontStyle: "italic"
        },
        "& table.pasted-table tr:hover td, & table tr:hover td": {
          backgroundColor: t => t.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)"
        },
        "& table.label-left td.left-label-cell": {
          fontWeight: 700,
          borderRight: "3px solid #333",
          backgroundColor: t => t.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"
        },
        "& table td.left-label-cell:empty::before": {
          content: "attr(data-placeholder)",
          opacity: 0.4,
          fontStyle: "italic"
        },
        "& em, & i": {
          fontStyle: "italic"
        },
        "& u": {
          textDecoration: "underline"
        },
        "& s, & strike": {
          textDecoration: "line-through"
        },
        "& img.editor-img": {
          maxWidth: "100%",
          height: "100%",
          width: "auto",
          display: "block",
          objectFit: "contain",
          cursor: "zoom-in",
          // Promote each image to its own compositor layer
          // so the rasterized bitmap is cached. Subsequent
          // scrolls reuse the layer instead of repainting.
          transform: "translateZ(0)",
          backfaceVisibility: "hidden"
        },
        // Sizes only apply when the image is NOT inside a row.
        "& :not(.editor-img-row) > .editor-img-wrap img.editor-img.size-small": {
          width: "150px",
          height: "auto"
        },
        "& :not(.editor-img-row) > .editor-img-wrap img.editor-img.size-medium": {
          width: "260px",
          height: "auto"
        },
        "& :not(.editor-img-row) > .editor-img-wrap img.editor-img.size-large": {
          width: "90%",
          height: "auto"
        },
        "& .editor-img-row": {
          display: "flex",
          gap: t => t.spacing(2),
          justifyContent: "flex-start",
          alignItems: "stretch",
          margin: t => t.spacing(2, 0),
          flexWrap: "nowrap",
          overflowX: "scroll",
          overflowY: "hidden",
          height: 320,
          border: t => `2px solid ${t.palette.grey[400]}`,
          borderRadius: 1.5,
          backgroundColor: t => t.palette.grey[100],
          padding: t => t.spacing(1),
          // Skip rendering rows that scroll off the
          // viewport. Once a row has been painted the
          // browser keeps the rasterized result and reuses
          // it instead of re-decoding/laying-out the inner
          // images on every scroll, which was costing ~2s
          // per scroll cycle on image-heavy pages.
          contentVisibility: "auto",
          // Reserve the row's vertical extent so skipped
          // rows don't collapse to 0 and trigger layout
          // jitter when the user scrolls them into view.
          containIntrinsicSize: "320px 320px",
          // Always-visible horizontal scrollbar to indicate more images
          "&::-webkit-scrollbar": {
            height: 10,
            WebkitAppearance: "none"
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: t => t.palette.grey[200],
            borderRadius: 5
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: t => t.palette.grey[500],
            borderRadius: 5
          },
          scrollbarWidth: "thin",
          scrollbarColor: t => `${t.palette.grey[500]} ${t.palette.grey[200]}`
        },
        // Strip chrome on single-image rows so the lone image
        // renders as a clean inline figure.
        "& .editor-img-row[data-image-count='1']": {
          border: "none",
          background: "transparent",
          padding: 0,
          height: "auto",
          overflow: "visible"
        },
        // Same for figure-wrapped single-image rows. Higher
        // specificity ensures it wins over later rules.
        "& .editor-img-figure > .editor-img-row[data-image-count='1']": {
          border: "none",
          background: "transparent",
          padding: 0,
          height: "auto",
          overflow: "visible"
        },
        // Defensive: any row carrying image text should never
        // show the row chrome, regardless of figure wrapping.
        "& .editor-img-row[data-image-text='true']": {
          border: "none",
          background: "transparent",
          padding: 0,
          height: "auto",
          overflow: "visible"
        },
        "& .editor-img-row .editor-img-wrap": {
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: 0,
          flexShrink: 0
        },
        "& .editor-img-row .editor-img-wrap img.editor-img": {
          height: "100%",
          width: "auto",
          maxWidth: "none",
          objectFit: "cover"
        },
        // Cap single-image rows so the lone image doesn't blow up.
        // Mirrors the editor's max-height (450px) for parity.
        "& .editor-img-row[data-image-count='1'] .editor-img-wrap img.editor-img": {
          height: "auto",
          maxHeight: 450,
          maxWidth: "100%",
          objectFit: "initial"
        },
        // Image figure (row + side caption text). Read-only render.
        "& .editor-img-figure": {
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          gap: t => t.spacing(2),
          margin: t => t.spacing(2, 0),
          width: "100%",
          // Stack caption below image on md and smaller viewports;
          // keep side-by-side row on lg and xl.
          "@media (max-width: 1199px)": {
            flexDirection: "column",
            alignItems: "stretch"
          }
        },
        "& .editor-img-figure[data-text-side='left']": {
          flexDirection: "row-reverse"
        },
        "& .editor-img-figure > .editor-img-row": {
          margin: 0,
          flexShrink: 1,
          flexGrow: 0,
          flexBasis: "60%",
          width: "60%",
          maxWidth: "60%",
          minWidth: 0,
          height: 450,
          "@media (max-width: 1199px)": {
            flexBasis: "auto",
            maxWidth: "100%",
            width: "100%"
          }
        },
        "& .editor-img-figure > .editor-img-row .editor-img-wrap": {
          width: "100%",
          height: "100%",
          minWidth: 0,
          cursor: "zoom-in"
        },
        // When the figure wraps the row, the lone image fills the
        // row's fixed height (cropping via cover is acceptable).
        // Higher specificity than the generic single-image rule.
        "& .editor-img-figure .editor-img-row[data-image-count='1'] .editor-img-wrap img.editor-img": {
          width: "100%",
          height: "100%",
          maxWidth: "100%",
          maxHeight: "none",
          objectFit: "cover",
          cursor: "zoom-in",
          display: "block"
        },
        "& .editor-img-text": {
          flex: "1 1 0",
          minWidth: 120,
          alignSelf: "stretch",
          padding: "10px",
          textAlign: "left",
          verticalAlign: "top",
          // Mirror the contenteditable's authoring behavior so
          // newlines and runs of spaces typed into the caption
          // are preserved in the published view.
          whiteSpace: "pre-wrap"
        },
        // Strip editor-only chrome from the published view.
        "& .img-text-swap-btn, & .img-text-remove-btn, & .img-row-add-text-btn": {
          display: "none"
        },
        "& .editor-img-wrap::after": {
          content: '"\\200A"',
          display: "inline"
        }
      },
      suppressHydrationWarning: true,
      ref: containerRef,
      dangerouslySetInnerHTML: {
        __html: normalizedHTML
      }
    }), /*#__PURE__*/_jsx(Modal, {
      open: activeIdx !== null,
      onClose: () => setActiveIdx(null),
      closeAfterTransition: true,
      slots: {
        backdrop: Backdrop
      },
      slotProps: {
        backdrop: {
          timeout: 200,
          sx: {
            backgroundColor: "rgba(0,0,0,0.85)"
          }
        }
      },
      children: /*#__PURE__*/_jsx(Fade, {
        in: activeIdx !== null,
        children: /*#__PURE__*/_jsxs(Box, {
          sx: {
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            outline: "none",
            p: {
              xs: 2,
              sm: 6
            }
          },
          onClick: () => setActiveIdx(null),
          children: [/*#__PURE__*/_jsx(IconButton, {
            onClick: e => {
              e.stopPropagation();
              setActiveIdx(null);
            },
            sx: {
              position: "fixed",
              top: 16,
              right: 16,
              color: "#fff",
              bgcolor: "rgba(0,0,0,0.4)",
              "&:hover": {
                bgcolor: "rgba(0,0,0,0.6)"
              }
            },
            "aria-label": "Close image viewer",
            children: /*#__PURE__*/_jsx(CloseIcon, {})
          }), images.length > 1 && /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(IconButton, {
              onClick: e => {
                e.stopPropagation();
                setActiveIdx(i => i === null ? null : (i - 1 + images.length) % images.length);
              },
              sx: {
                position: "fixed",
                left: 16,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#fff",
                bgcolor: "rgba(0,0,0,0.4)",
                "&:hover": {
                  bgcolor: "rgba(0,0,0,0.6)"
                }
              },
              "aria-label": "Previous image",
              children: /*#__PURE__*/_jsx(ChevronLeftIcon, {
                fontSize: "large"
              })
            }), /*#__PURE__*/_jsx(IconButton, {
              onClick: e => {
                e.stopPropagation();
                setActiveIdx(i => i === null ? null : (i + 1) % images.length);
              },
              sx: {
                position: "fixed",
                right: 16,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#fff",
                bgcolor: "rgba(0,0,0,0.4)",
                "&:hover": {
                  bgcolor: "rgba(0,0,0,0.6)"
                }
              },
              "aria-label": "Next image",
              children: /*#__PURE__*/_jsx(ChevronRightIcon, {
                fontSize: "large"
              })
            })]
          }), activeIdx !== null && images[activeIdx] && /*#__PURE__*/_jsx(Box, {
            component: "img",
            src: images[activeIdx].src,
            alt: images[activeIdx].alt,
            onClick: e => e.stopPropagation(),
            sx: {
              maxWidth: "90vw",
              maxHeight: "90vh",
              objectFit: "contain",
              boxShadow: 24,
              borderRadius: 1
            }
          })]
        })
      })
    })]
  });
}